/**
 * clipProcessorDepsFromAdapter.ts
 *
 * Builds IClipProcessorDeps from a TranscriptSpeakersAdapter instance.
 * Used by TranscriptSpeakersTab clip creation (Step 10) to provide
 * AIClipProcessor with a narrow dependency bag where:
 *
 *  - getTranscriptFallback() reads from the **scoped editor** (not global transcriptionStore)
 *  - getKaraokeContext() returns null (no karaoke track in transcript tab)
 *  - Shared infra (story, persistence, identity, flags) delegates to the real store
 *
 * See: docs/refactors/transcript-dependency-injection.md (Step 9d)
 */

import type { TranscriptSpeakersAdapter } from './TranscriptSpeakersAdapter';
import type { IClipProcessorDeps } from '@src/services/IClipProcessorDeps';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * Build IClipProcessorDeps from a TranscriptSpeakersAdapter.
 *
 * The adapter exposes `realVideoCreator` (readonly) for shared infrastructure
 * and `getScopedEditor()` for the independent transcript editing context.
 */
export function clipProcessorDepsFromAdapter(
  adapter: TranscriptSpeakersAdapter,
): IClipProcessorDeps {
  const realStore = adapter.realVideoCreator;

  return {
    prepareAsCurrentLoadedVideo: (originalVideo, title, sourcePlatform) =>
      realStore.prepareAsCurrentLoadedVideo(
        originalVideo,
        title,
        sourcePlatform,
      ),

    get story() {
      return adapter.story ?? undefined;
    },

    get currentEditor() {
      return realStore.rootStore.userIdentityStore.currentEditor;
    },

    // ── KEY DIFFERENCE: reads from scoped editor, not global store ──
    getTranscriptFallback: () => {
      const scopedEditor = adapter.getScopedEditor();
      const elements = scopedEditor.editor.getFullTranscript();
      // The editor returns ActiveTranscriptToken[] which is a superset of
      // TranscriptElement[]. AIClipProcessor only reads standard fields
      // (value, ts, end_ts, type, state, etc.) so the cast is safe.
      return {
        final: elements as unknown as TranscriptElement[],
        original: elements as unknown as TranscriptElement[],
      };
    },

    get transcriptLanguage() {
      return realStore.rootStore.transcriptionStore.rawLanguage;
    },

    // Karaoke is irrelevant in the transcript tab — no karaoke track
    getKaraokeContext: () => null,

    get videoRepository() {
      return realStore.datoClientStore?.videoRepository ?? undefined;
    },

    get flags() {
      return {
        enableTranscriptSnapshotResourceUpload:
          !!realStore.flags?.enableTranscriptSnapshotResourceUpload,
        enableTranscriptSnapshotResourceDualWrite:
          !!realStore.flags?.enableTranscriptSnapshotResourceDualWrite,
      };
    },

    getMigrationLog: () => {
      return undefined;
    },

    isOriginalVideoElement: (elementState: unknown) =>
      realStore.isOriginalVideoElement(elementState),

    get sceneDetections() {
      return realStore.sceneDetections;
    },

    addMultipleVideosToStory: videos =>
      realStore.addMultipleVideosToStory(videos),
  };
}
